/**
 * 案件起票（Project Intake）フォームの型定義。
 *
 * MyPage の「＋案件を起票する」モーダルが送受信するペイロード形状。
 * バックエンド（POST /project-intake/projects）は Backlog チケット作成 +
 * Slack チャンネル作成 + 案件仕様書コピー（GAS）を実行し、各リンクを返す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件起票フォームの入力・選択肢・作成結果の型定義
 */

/**
 * 案件種類。後続のチケット作成ロジック（生成する子チケット種別）を分岐させる。
 *
 * 「軽タスク」は文言/画像差し替え/軽微な UI 調整など小さな作業を指す種別。
 * チケット構成は「開発のみ」と同じ（開発ツリーのみ）で、案件タイトル + やりたいこと（目的）を
 * AI で判定し、軽タスク基準を満たせば Backlog カスタムフィールド「軽タスクフラグ」を立てる。
 */
export type ProjectKind = '開発のみ' | 'デザインのみ' | '両方' | '軽タスク';

/** 案件種類の選択肢（プルダウン表示順）。 @graph-connects none */
export const PROJECT_KINDS: readonly ProjectKind[] = [
  '開発のみ',
  'デザインのみ',
  '両方',
  '軽タスク',
];

/** 軽タスク種別か（Step2 の「目的」ラベルを「やりたいこと」に切り替える等の分岐に使う）。 @graph-connects none */
export function isLightTaskKind(kind: ProjectKind | ''): boolean {
  return kind === '軽タスク';
}

/** PL / 主担当プルダウンの 1 メンバー。nickname を値、email を解決キーとして持つ。 */
export interface IntakeMemberOption {
  /** メンバーの nickname（workspace.unified_members.nick_name） */
  nickname: string;
  /** メンバーの email（nickname → email/slack/backlog 解決の基点） */
  email: string;
}

/**
 * 起票フォームのプルダウン選択肢。team master シート + workspace.unified_members 由来。
 */
export interface ProjectIntakeOptions {
  /** 事業の一覧（team master の「事業」列 distinct） */
  businesses: string[];
  /** 事業ごとのチーム一覧（team master の 事業×チーム グリッド） */
  teamsByBusiness: Record<string, string[]>;
  /** PL / 主担当に選べる active メンバー一覧 */
  members: IntakeMemberOption[];
}

/**
 * プロジェクト憲章（Step 2）。すべて任意。空欄でも起票でき、後から案件仕様書で追記する。
 * 各値は案件仕様書の対応セルへ回填される（バックエンド → GAS）。
 */
export interface ProjectCharterInput {
  /** 目的 */
  purpose?: string;
  /** 背景 */
  background?: string;
  /** 目標 */
  goal?: string;
  /** 成果物 */
  deliverables?: string;
  /** 前提条件 */
  preconditions?: string;
  /** 制約条件 */
  constraints?: string;
  /** 概算スケジュール */
  schedule?: string;
  /** 概算コスト */
  cost?: string;
  /** 概算体制 */
  structure?: string;
  /** 備考 */
  notes?: string;
}

/**
 * 起票リクエスト。releaseHopeDate 以外の値は案件仕様書へ回填される。
 */
export interface CreateProjectInput {
  /** 案件種類 */
  kind: ProjectKind;
  /** 事業 */
  business: string;
  /** 担当チーム */
  team: string;
  /** PL の nickname */
  plNickname: string;
  /** 主担当の nickname */
  mainWorkerNickname: string;
  /** PJ 名 */
  projectName: string;
  /** AB テスト案件か */
  isAbTest: boolean;
  /**
   * リリース希望日（YYYY-MM-DD）。案件仕様書には回填せず、
   * チケット作成後に Backlog カスタムフィールド id=221474「リリース希望日」へ直接反映する。
   */
  releaseHopeDate: string | null;
  /** ラフイメージ・機能の URL（任意）。案件仕様書の依頼シートへ回填する。 */
  roughImageUrl?: string;
  /** プロジェクト憲章（Step 2、任意） */
  charter: ProjectCharterInput;
}

/**
 * 起票結果。作成された各リソースへのリンク。
 */
export interface CreateProjectResponse {
  /** Backlog 課題キー（例: AIRCLOSET-12345） */
  backlogKey: string;
  /** Backlog 親チケット URL */
  backlogUrl: string;
  /** 作成された Slack チャンネル URL */
  slackChannelUrl: string;
  /** 作成された案件仕様書 URL */
  specSheetUrl: string;
  /**
   * ローカル dry-run（LOCAL_DRY_RUN_DEFAULT）で作成された結果か。
   * true のとき各 URL はダミー（#dry-run-*）で、実リソースは作成されていない。
   * 本番は未設定=false のため常に false。
   */
  dryRun: boolean;
}
